import { useState, useEffect } from "react";
import { FaUpload, FaTimes, FaPlay, FaImage, FaFilm, FaTrash, FaSpinner } from "react-icons/fa";
import axios from "axios";

const CreateBanner = () => {
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    type: "images"
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      filePreviews.forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [filePreviews]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear any previous errors
    setError("");
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate based on type
    if (formData.type === "video") {
      if (files.length > 1) {
        setError("Only one video file can be uploaded");
        return;
      }
      const file = files[0];
      if (!file.type.startsWith("video/")) {
        setError("Please select a valid video file");
        return;
      }
      if (file.size > 100 * 1024 * 1024) { // 100MB limit
        setError("Video file size should be less than 100MB");
        return;
      }
    } else {
      // Images validation
      const invalidFiles = files.filter(file => !file.type.startsWith("image/"));
      if (invalidFiles.length > 0) {
        setError("Please select only image files");
        return;
      }
      const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024); // 5MB limit
      if (oversizedFiles.length > 0) {
        setError("Each image should be less than 5MB");
        return;
      }
    }

    // Clear old previews
    filePreviews.forEach(preview => URL.revokeObjectURL(preview));
    
    // Create new previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    
    setSelectedFiles(files);
    setFilePreviews(newPreviews);
    setError("");
  };

  // Remove selected file
  const removeFile = (index) => {
    URL.revokeObjectURL(filePreviews[index]);
    
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = filePreviews.filter((_, i) => i !== index);
    
    setSelectedFiles(newFiles);
    setFilePreviews(newPreviews);
  };

  // Clear all files
  const clearAllFiles = () => {
    filePreviews.forEach(preview => URL.revokeObjectURL(preview));
    setSelectedFiles([]);
    setFilePreviews([]);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title) {
      setError("Please enter a title");
      return;
    }
    
    if (selectedFiles.length === 0) {
      setError(formData.type === "video" ? "Please select a video file" : "Please select at least one image");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Create FormData
      const formDataToSend = new FormData();
      
      // Append form fields
      formDataToSend.append("title", formData.title);
      formDataToSend.append("subtitle", formData.subtitle || "");
      formDataToSend.append("type", formData.type);
      
      // Append files
      selectedFiles.forEach(file => {
        formDataToSend.append("files", file);
      });

      // Send request
      const response = await axios.post(
        "https://api.dreamtoday.in/api/admin/createbanner",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );

      if (response.data && response.data.success) {
        setSuccess("Banner created successfully!");
        
        // Reset form
        setFormData({
          title: "",
          subtitle: "",
          type: "images"
        });
        clearAllFiles();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(response.data.message || "Failed to create banner");
      }
    } catch (error) {
      console.error("Error creating banner:", error);
      setError(error.response?.data?.message || "Failed to create banner");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Create New Banner</h1>
          <p className="mt-2 text-gray-600">Create banners for your application</p>
        </div>

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8">
            {/* Error/Success Messages */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                <div className="flex items-center">
                  <FaTimes className="text-red-500 mr-2" />
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            )}
            
            {success && (
              <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                <div className="flex items-center">
                  <FaUpload className="text-green-500 mr-2" />
                  <p className="text-green-700">{success}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banner Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter banner title"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtitle (Optional)
                </label>
                <input
                  type="text"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleInputChange}
                  placeholder="Enter banner subtitle"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              {/* Banner Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Banner Type *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Images Option */}
                  <label
                    className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.type === "images"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="images"
                      checked={formData.type === "images"}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      formData.type === "images" ? "bg-blue-500" : "bg-gray-100"
                    }`}>
                      <FaImage className={formData.type === "images" ? "text-white" : "text-gray-500"} />
                    </div>
                    <div className="ml-4">
                      <p className={`font-medium ${
                        formData.type === "images" ? "text-blue-700" : "text-gray-700"
                      }`}>
                        Images Banner
                      </p>
                      <p className="text-sm text-gray-500">
                        Upload multiple images (Max 5MB each)
                      </p>
                    </div>
                  </label>

                  {/* Video Option */}
                  <label
                    className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.type === "video"
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="video"
                      checked={formData.type === "video"}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      formData.type === "video" ? "bg-purple-500" : "bg-gray-100"
                    }`}>
                      <FaFilm className={formData.type === "video" ? "text-white" : "text-gray-500"} />
                    </div>
                    <div className="ml-4">
                      <p className={`font-medium ${
                        formData.type === "video" ? "text-purple-700" : "text-gray-700"
                      }`}>
                        Video Banner
                      </p>
                      <p className="text-sm text-gray-500">
                        Upload single video (Max 100MB)
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.type === "video" ? "Upload Video *" : "Upload Images *"}
                </label>
                
                {/* Upload Area */}
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-500 transition-colors">
                  <div className="space-y-1 text-center">
                    {formData.type === "video" ? (
                      <FaFilm className="mx-auto h-12 w-12 text-gray-400" />
                    ) : (
                      <FaImage className="mx-auto h-12 w-12 text-gray-400" />
                    )}
                    
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                      >
                        <span>Upload files</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          accept={formData.type === "video" ? "video/*" : "image/*"}
                          multiple={formData.type === "images"}
                          onChange={handleFileSelect}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {formData.type === "video" 
                        ? "MP4, WebM, Ogg up to 100MB"
                        : "PNG, JPG, GIF up to 5MB each"
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* File Previews */}
              {filePreviews.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      {formData.type === "video" ? "Video Preview" : "Image Previews"}
                    </label>
                    <button
                      type="button"
                      onClick={clearAllFiles}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <FaTrash size={12} /> Clear all
                    </button>
                  </div>
                  
                  <div className={`grid gap-4 ${
                    formData.type === "video" 
                      ? "grid-cols-1" 
                      : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                  }`}>
                    {filePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        {formData.type === "video" ? (
                          <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                            <video
                              src={preview}
                              controls
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        
                        {/* Overlay with file info */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-xs mb-2">
                              {selectedFiles[index]?.name}
                            </p>
                            <p className="text-white text-xs mb-2">
                              {(selectedFiles[index]?.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition"
                            >
                              <FaTimes size={12} />
                            </button>
                          </div>
                        </div>
                        
                        {/* Type badge */}
                        <span className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                          {formData.type === "video" ? "Video" : `Image ${index + 1}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Creating Banner...
                    </>
                  ) : (
                    <>
                      <FaUpload />
                      Create Banner
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
          <h3 className="text-sm font-medium text-blue-800 mb-2">📋 Banner Creation Guidelines</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Choose between Image Banner or Video Banner</li>
            <li>• For Images: Upload multiple images (max 5MB each)</li>
            <li>• For Video: Upload single video file (max 100MB)</li>
            <li>• Title is required, subtitle is optional</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateBanner;